import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  TextField,
  Chip,
  Tooltip,
  LinearProgress,
  Grid,
  Card,
  Alert,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Pause,
  SkipNext,
  SkipPrevious,
  Save,
  Close,
  CheckCircle,
  VolumeUp,
  VolumeDown,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import axios from 'axios';
import { useColorMode } from '../contexts/ColorModeContext';
import { readThemeColor } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;
const WAVEFORM_CACHE_LIMIT = 40;
const waveformCache = new Map();
const waveformInflight = new Map();

function waveformCacheKey(assetId, revision) {
  return `${assetId}:${revision || ''}`;
}

function rememberWaveform(key, peaks) {
  if (!key || !peaks?.length) return;
  waveformCache.set(key, peaks);
  while (waveformCache.size > WAVEFORM_CACHE_LIMIT) {
    const oldest = waveformCache.keys().next().value;
    waveformCache.delete(oldest);
  }
}

function getCachedWaveform(assetId, revision) {
  const precise = waveformCacheKey(assetId, revision);
  if (waveformCache.has(precise)) return waveformCache.get(precise);
  const prefix = `${assetId}:`;
  for (const [key, peaks] of waveformCache) {
    if (key === prefix || key.startsWith(prefix)) return peaks;
  }
  return null;
}

function abortError() {
  const error = new Error('Aborted');
  error.name = 'AbortError';
  return error;
}

async function fetchAssetWaveform(assetId, revision, signal) {
  const cached = getCachedWaveform(assetId, revision);
  if (cached) return cached;
  if (signal?.aborted) throw abortError();

  const key = waveformCacheKey(assetId, revision);
  let job = waveformInflight.get(key);
  if (!job) {
    job = (async () => {
      const url = `${API_URL}/api/assets/${assetId}/waveform${revision ? `?t=${encodeURIComponent(revision)}` : ''}`;
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) {
        throw new Error(`Waveform HTTP ${response.status}`);
      }
      const data = await response.json();
      const peaks = Array.isArray(data.peaks) ? data.peaks : [];
      if (!peaks.length) throw new Error('Empty waveform');
      rememberWaveform(key, peaks);
      return peaks;
    })().finally(() => {
      waveformInflight.delete(key);
    });
    waveformInflight.set(key, job);
  }

  if (!signal) return job;

  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError());
    if (signal.aborted) {
      onAbort();
      return;
    }
    signal.addEventListener('abort', onAbort, { once: true });
    job.then(
      (peaks) => {
        signal.removeEventListener('abort', onAbort);
        resolve(peaks);
      },
      (error) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      }
    );
  });
}

function buildEditorAudioUrl(asset, { useAssetStream, revision }) {
  if (useAssetStream && asset?.id) {
    let url = `${API_URL}/api/assets/${asset.id}/stream`;
    if (revision) url += `?t=${encodeURIComponent(revision)}`;
    return url;
  }
  let audioUrl = asset?.filepath || asset?.file_path;
  if (!audioUrl) return null;
  if (!audioUrl.startsWith('http')) {
    audioUrl = audioUrl.startsWith('/')
      ? `${API_URL}${audioUrl}`
      : `${API_URL}/uploads/${audioUrl}`;
  }
  if (revision) {
    audioUrl += `${audioUrl.includes('?') ? '&' : '?'}t=${encodeURIComponent(revision)}`;
  }
  return audioUrl;
}

function peaksFromChannelData(channelData, samples = 8000) {
  const length = channelData.length;
  const count = Math.min(samples, length);
  const blockSize = Math.max(1, Math.floor(length / count));
  const step = Math.max(1, Math.floor(blockSize / 32));
  const filtered = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    const start = i * blockSize;
    const end = Math.min(start + blockSize, length);
    let max = 0;
    for (let j = start; j < end; j += step) {
      const value = Math.abs(channelData[j]);
      if (value > max) max = value;
    }
    filtered[i] = max;
  }
  return Array.from(filtered);
}

/**
 * Audio Editor Component
 * Allows editing of audio markers: Cue In, Intro, Start Next, Fade Out, Cue Out
 */
export default function AudioEditor({
  asset,
  isOpen,
  onClose,
  onSave,
  saveDirectly = true,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  markerMode = 'all',
  preloadAssets = [],
}) {
  const { mode: colorMode } = useColorMode();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showMarkerReview, setShowMarkerReview] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(100);
  const [waveformData, setWaveformData] = useState(null);
  const [audioBuffer, setAudioBuffer] = useState(null); // Store full audio buffer for high-res waveform
  const [zoom, setZoom] = useState(1); // Zoom level (1 = 100%, 2 = 200%, etc.)
  const [zoomOffset, setZoomOffset] = useState(0); // Offset for panning when zoomed
  const zoomRef = useRef(1);
  const zoomOffsetRef = useRef(0);

  // Audio markers
  const [markers, setMarkers] = useState({
    cue_in: 0,
    intro: null,
    start_next: null,
    fade_out_start: null,
    cue_out: 0,
  });

  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const animationFrameRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const isDraggingRef = useRef(false);
  const isMouseDownRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartOffsetRef = useRef(0);
  const loadGenerationRef = useRef(0);
  const loadAbortRef = useRef(null);
  const cueOnly = markerMode === 'cue';
  // Caches the expensive visible-range peak computation so the playhead can be
  // redrawn every frame during playback without regenerating the waveform.
  const peaksCacheRef = useRef({ key: null, peaks: null, normalizationPeak: 1 });

  const setZoomSynced = useCallback((nextZoom) => {
    zoomRef.current = nextZoom;
    setZoom(nextZoom);
  }, []);

  const setZoomOffsetSynced = useCallback((nextOffset) => {
    zoomOffsetRef.current = nextOffset;
    setZoomOffset(nextOffset);
  }, []);

  const getTimeFromCanvasX = useCallback((canvasX) => {
    const canvas = canvasRef.current;
    if (!canvas || !duration) return 0;

    const width = canvas.width;
    const currentZoom = zoomRef.current;
    const currentZoomOffset = zoomOffsetRef.current;
    const totalVirtualWidth = width * currentZoom;
    if (!totalVirtualWidth) return 0;

    const startTime = (currentZoomOffset / totalVirtualWidth) * duration;
    const endTime = ((currentZoomOffset + width) / totalVirtualWidth) * duration;
    const clampedX = Math.max(0, Math.min(width, canvasX));
    const normalizedX = width > 0 ? clampedX / width : 0;
    const timeAtX = startTime + normalizedX * (endTime - startTime);

    return Math.max(0, Math.min(duration, timeAtX));
  }, [duration]);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    zoomOffsetRef.current = zoomOffset;
  }, [zoomOffset]);

  // Reset saved state whenever markers are changed by the user
  useEffect(() => {
    setSaved(false);
  }, [markers]);

  const assetFilepath = asset?.filepath || asset?.file_path;
  const assetId = asset?.id;
  const assetDuration = asset?.duration;
  const assetRevision = asset?.updated_at || asset?.file_size || '';

  // Initialize when asset changes
  useEffect(() => {
    if (!isOpen || !asset) return undefined;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setIsPlaying(false);

    const sourceMarkers = asset.markers && typeof asset.markers === 'object' ? asset.markers : asset;
    setMarkers({
      cue_in: sourceMarkers.cue_in || 0,
      intro: sourceMarkers.intro || null,
      start_next: sourceMarkers.start_next || null,
      fade_out_start: sourceMarkers.fade_out_start || sourceMarkers.extro || null,
      cue_out: sourceMarkers.cue_out || assetDuration || 0,
    });
    setDuration(assetDuration || 0);
    setCurrentTime(sourceMarkers.cue_in || 0);
    setShowMarkerReview(!!asset.markers_need_review);
    setZoomSynced(1);
    setZoomOffsetSynced(0);
    peaksCacheRef.current = { key: null, peaks: null, normalizationPeak: 1 };
  }, [isOpen, assetId, assetDuration, assetRevision, setZoomSynced, setZoomOffsetSynced]);

  // Load audio only when the file identity changes. Duration updates must not
  // abort an in-flight waveform fetch or clear the <audio> element.
  useEffect(() => {
    if (!isOpen || !assetId) {
      return undefined;
    }

    setWaveformData(null);
    setAudioBuffer(null);
    loadAudio();

    return () => {
      if (loadAbortRef.current) {
        loadAbortRef.current.abort();
        loadAbortRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [isOpen, assetId, assetFilepath, assetRevision]);

  const attachAudioElement = (audioUrl) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    const audio = new Audio();
    audio.preload = 'auto';
    audio.volume = volume / 100;
    const isCurrent = () => audioRef.current === audio;
    audio.addEventListener('loadedmetadata', () => {
      if (!isCurrent()) return;
      setDuration(audio.duration);
      const knownCueOut = asset?.cue_out || asset?.duration || 0;
      if (!knownCueOut || knownCueOut === 0) {
        setMarkers((prev) => ({ ...prev, cue_out: audio.duration }));
      }
    });
    audio.addEventListener('timeupdate', () => {
      if (!isCurrent()) return;
      setCurrentTime(audio.currentTime);
    });
    audio.addEventListener('ended', () => {
      if (!isCurrent()) return;
      setIsPlaying(false);
      setCurrentTime(0);
    });
    audio.src = audioUrl;
    audioRef.current = audio;
    return audio;
  };

  const decodeWaveformFromAudioUrl = async (audioUrl, signal) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
    const response = await fetch(audioUrl, { credentials: 'include', signal });
    if (!response.ok) {
      throw new Error(`Audio HTTP ${response.status}`);
    }
    const arrayBuffer = await response.arrayBuffer();
    const decodedBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
    return {
      buffer: decodedBuffer,
      peaks: peaksFromChannelData(decodedBuffer.getChannelData(0)),
    };
  };

  // Stream playback immediately; pull a tiny cached peak envelope instead of
  // downloading and decoding the full FLAC/Opus file in the browser.
  const loadAudio = async () => {
    const useAssetStream = saveDirectly && Boolean(asset?.id);
    const audioUrl = buildEditorAudioUrl(asset, { useAssetStream, revision: assetRevision });
    if (!audioUrl) return;

    const generation = loadGenerationRef.current + 1;
    loadGenerationRef.current = generation;
    if (loadAbortRef.current) loadAbortRef.current.abort();
    const abort = new AbortController();
    loadAbortRef.current = abort;

    const cachedPeaks = useAssetStream && asset?.id
      ? getCachedWaveform(asset.id, assetRevision)
      : null;
    if (!cachedPeaks) setLoading(true);
    try {
      attachAudioElement(audioUrl);

      let peaks = cachedPeaks;
      let decodedBuffer = null;
      if (!peaks && useAssetStream) {
        try {
          peaks = await fetchAssetWaveform(asset.id, assetRevision, abort.signal);
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.warn('Asset waveform API failed; showing editor without a decoded waveform:', error);
        }
      }

      if (!peaks && !useAssetStream) {
        const decoded = await decodeWaveformFromAudioUrl(audioUrl, abort.signal);
        peaks = decoded.peaks;
        decodedBuffer = decoded.buffer;
      }

      if (generation !== loadGenerationRef.current) return;
      if (decodedBuffer) setAudioBuffer(decodedBuffer);
      if (peaks) setWaveformData(peaks);
      setLoading(false);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error loading audio:', error);
      if (generation === loadGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  const preloadKey = (preloadAssets || [])
    .filter((item) => item?.id)
    .map((item) => `${item.id}:${item.updated_at || item.file_size || ''}`)
    .join('|');
  useEffect(() => {
    if (!isOpen || !saveDirectly || !preloadKey) return undefined;
    (preloadAssets || []).forEach((item) => {
      if (!item?.id) return;
      fetchAssetWaveform(item.id, item.updated_at || item.file_size || '').catch(() => {});
    });
    return undefined;
  }, [isOpen, saveDirectly, preloadKey]);



  // Build one amplitude value per canvas pixel for the visible range.
  // Multi-sample columns use max(|sample|) so the envelope stays dense but stable;
  // sub-sample zoom linearly interpolates so the line stays continuous instead of blocky.
  const generateHighResWaveform = useCallback((startTime, endTime, pixelWidth) => {
    if (!audioBuffer || !duration || !pixelWidth || pixelWidth < 1) {
      return null;
    }

    const channelData = audioBuffer.getChannelData(0);
    const sampleRate = audioBuffer.sampleRate;
    const totalSamples = channelData.length;

    const clampedStartTime = Math.max(0, Math.min(startTime, duration));
    const clampedEndTime = Math.max(clampedStartTime, Math.min(endTime, duration));
    const startSample = clampedStartTime * sampleRate;
    const endSample = Math.min(clampedEndTime * sampleRate, totalSamples);
    const visibleSamples = endSample - startSample;

    if (visibleSamples <= 0) {
      return null;
    }

    const peaks = new Float32Array(pixelWidth);
    const samplesPerPixel = visibleSamples / pixelWidth;

    if (samplesPerPixel >= 1) {
      for (let x = 0; x < pixelWidth; x++) {
        const sampleStart = Math.floor(startSample + x * samplesPerPixel);
        const sampleEnd = Math.min(
          Math.floor(startSample + (x + 1) * samplesPerPixel),
          totalSamples
        );
        let max = 0;
        for (let j = Math.max(0, sampleStart); j < sampleEnd; j++) {
          const v = Math.abs(channelData[j]);
          if (v > max) max = v;
        }
        peaks[x] = max;
      }
    } else {
      // Fewer than one audio sample per pixel: interpolate for a continuous curve.
      for (let x = 0; x < pixelWidth; x++) {
        const samplePos = startSample + x * samplesPerPixel;
        const i0 = Math.max(0, Math.min(totalSamples - 1, Math.floor(samplePos)));
        const i1 = Math.min(totalSamples - 1, i0 + 1);
        const t = samplePos - Math.floor(samplePos);
        const v0 = Math.abs(channelData[i0] || 0);
        const v1 = Math.abs(channelData[i1] || 0);
        peaks[x] = v0 * (1 - t) + v1 * t;
      }
    }

    return peaks;
  }, [audioBuffer, duration]);

  // Draw waveform with markers
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !duration) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerY = height / 2;

    // Clear canvas
    ctx.fillStyle = readThemeColor('--rs-bg', '#0a0c11');
    ctx.fillRect(0, 0, width, height);

    // Calculate visible time range based on zoom and pan
    // Think of it as: totalVirtualWidth = width * zoom
    // We're viewing pixels from zoomOffset to zoomOffset + width
    const totalVirtualWidth = width * zoom;
    const rawStartTime = (zoomOffset / totalVirtualWidth) * duration;
    const rawEndTime = ((zoomOffset + width) / totalVirtualWidth) * duration;
    const startTime = Math.max(0, Math.min(rawStartTime, duration));
    const endTime = Math.max(0, Math.min(rawEndTime, duration));
    
    // Helper to convert time to X position accounting for zoom and pan
    const timeToX = (time) => {
      // Clamp time to valid range
      const clampedTime = Math.max(0, Math.min(time, duration));
      
      // Calculate the virtual X position (as if entire track is rendered at current zoom)
      const virtualX = (clampedTime / duration) * totalVirtualWidth;
      
      // Subtract the pan offset to get actual canvas position
      const x = virtualX - zoomOffset;
      
      return x;
    };

    // Draw grid (more lines when zoomed)
    ctx.strokeStyle = readThemeColor('--rs-border', '#2a3342');
    ctx.lineWidth = 1;
    const gridLines = Math.max(10, Math.floor(10 * zoom));
    for (let i = 0; i <= gridLines; i++) {
      const time = startTime + ((endTime - startTime) / gridLines) * i;
      const x = timeToX(time);
      if (x >= 0 && x <= width) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
    }
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(width, centerY);
    ctx.stroke();

    // One peak per pixel for the visible range. Cached so playback can redraw
    // the playhead every frame without regenerating the envelope.
    const cacheKey = `${zoom}|${Math.round(zoomOffset)}|${duration}|${width}|${audioBuffer ? 'hb' : 'nb'}|${waveformData ? waveformData.length : 0}`;
    let peaks;
    let normalizationPeak;

    if (peaksCacheRef.current.key === cacheKey && peaksCacheRef.current.peaks) {
      peaks = peaksCacheRef.current.peaks;
      normalizationPeak = peaksCacheRef.current.normalizationPeak;
    } else {
      if (audioBuffer) {
        peaks = generateHighResWaveform(startTime, endTime, width) || waveformData || [];
      } else {
        // Fallback: resample the coarse overview into pixel columns
        const source = waveformData || [];
        if (source.length === 0) {
          peaks = null;
        } else {
          const resampled = new Float32Array(width);
          const viewStart = zoomOffset / totalVirtualWidth;
          const viewEnd = (zoomOffset + width) / totalVirtualWidth;
          for (let x = 0; x < width; x++) {
            const t = viewStart + ((viewEnd - viewStart) * x) / Math.max(1, width - 1);
            const srcPos = t * (source.length - 1);
            const i0 = Math.max(0, Math.min(source.length - 1, Math.floor(srcPos)));
            const i1 = Math.min(source.length - 1, i0 + 1);
            const frac = srcPos - i0;
            resampled[x] = (source[i0] || 0) * (1 - frac) + (source[i1] || 0) * frac;
          }
          peaks = resampled;
        }
      }

      if (peaks && peaks.length > 0) {
        // Light 3-tap smooth keeps neighboring pixel columns from looking jagged
        // without erasing real transients.
        if (peaks.length > 2) {
          const smoothed = new Float32Array(peaks.length);
          smoothed[0] = peaks[0];
          smoothed[peaks.length - 1] = peaks[peaks.length - 1];
          for (let i = 1; i < peaks.length - 1; i++) {
            smoothed[i] = peaks[i - 1] * 0.25 + peaks[i] * 0.5 + peaks[i + 1] * 0.25;
          }
          peaks = smoothed;
        }

        // Scale each visible segment so quiet files still use the available waveform height.
        const sortedPeaks = Array.from(peaks).sort((a, b) => a - b);
        const peakCeiling = sortedPeaks.length
          ? sortedPeaks[Math.max(0, Math.ceil(sortedPeaks.length * 0.98) - 1)]
          : 0;
        normalizationPeak = Math.max(peakCeiling, 0.0001);

        peaksCacheRef.current = { key: cacheKey, peaks, normalizationPeak };
      }
    }

    if (peaks && peaks.length > 0) {
      // Draw a mirrored envelope with one point per pixel and rounded joins so
      // zoomed views stay continuous instead of looking like discrete bars.
      ctx.strokeStyle = '#22d3ee';
      ctx.fillStyle = '#22d3ee40';
      ctx.lineWidth = 1.5;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';

    const peakCount = peaks.length;
    const ampAt = (i) => {
      const normalizedPeak = Math.min(1, (peaks[i] || 0) / normalizationPeak);
      return normalizedPeak * (height / 2) * 0.9;
    };

    ctx.beginPath();
    ctx.moveTo(0, centerY - ampAt(0));
    for (let i = 1; i < peakCount; i++) {
      ctx.lineTo(i, centerY - ampAt(i));
    }
    for (let i = peakCount - 1; i >= 0; i--) {
      ctx.lineTo(i, centerY + ampAt(i));
    }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    // Draw markers (only if visible in zoomed view, with smart label positioning)
    const isMarkerVisible = (x) => x >= -50 && x <= width + 50; // Add padding for labels

    // Collect all markers with their positions for smart label placement
    const markerPositions = [];
    
    // Cue In (green)
    if (markers.cue_in !== null && markers.cue_in >= 0) {
      const x = timeToX(markers.cue_in);
      if (isMarkerVisible(x)) {
        markerPositions.push({ x, color: '#4caf50', label: 'Cue In', time: markers.cue_in });
      }
    }

    // Intro (blue)
    if (!cueOnly && markers.intro !== null && markers.intro >= 0) {
      const x = timeToX(markers.intro);
      if (isMarkerVisible(x)) {
        markerPositions.push({ x, color: '#2196f3', label: 'Intro', time: markers.intro });
      }
    }

    // Start Next (orange)
    if (!cueOnly && markers.start_next !== null && markers.start_next >= 0) {
      const x = timeToX(markers.start_next);
      if (isMarkerVisible(x)) {
        markerPositions.push({ x, color: '#ff9800', label: 'Start Next', time: markers.start_next });
      }
    }

    // Fade Out Start (yellow)
    if (!cueOnly && markers.fade_out_start !== null && markers.fade_out_start >= 0) {
      const x = timeToX(markers.fade_out_start);
      if (isMarkerVisible(x)) {
        markerPositions.push({ x, color: '#ffc107', label: 'Fade Out', time: markers.fade_out_start });
      }
    }

    // Cue Out (red)
    if (markers.cue_out !== null && markers.cue_out >= 0) {
      const x = timeToX(markers.cue_out);
      if (isMarkerVisible(x)) {
        markerPositions.push({ x, color: '#f44336', label: 'Cue Out', time: markers.cue_out });
      }
    }

    // Sort markers by X position
    markerPositions.sort((a, b) => a.x - b.x);

    // Draw markers with smart label positioning to avoid overlaps
    markerPositions.forEach((marker, index) => {
      const { x, color, label } = marker;
      
      // Draw marker line (thicker when zoomed for visibility)
      ctx.strokeStyle = color;
      ctx.lineWidth = zoom > 4 ? 3 : 2;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();

      // Smart label positioning to avoid overlaps
      ctx.font = 'bold 12px sans-serif';
      const textWidth = ctx.measureText(label).width;
      let labelX = x + 5;
      let labelY = 15;
      
      // Check if label would go off screen to the right
      if (labelX + textWidth > width) {
        labelX = x - textWidth - 5;
      }
      // Check if label would go off screen to the left
      if (labelX < 0) {
        labelX = 5;
        // If still overlapping, stack vertically
        labelY = 15 + (index * 15);
      }
      
      // Check for overlaps with previous markers
      if (index > 0) {
        const prevMarker = markerPositions[index - 1];
        const minSpacing = 100; // Minimum pixels between markers to avoid overlap
        if (Math.abs(x - prevMarker.x) < minSpacing) {
          // Markers are close, stack labels vertically
          labelY = 15 + (index * 15);
          // Try to position label to the left if there's space
          if (x - textWidth - 5 > 0) {
            labelX = x - textWidth - 5;
          }
        }
      }

      // Draw label background for readability
      ctx.fillStyle = readThemeColor('--rs-bg', '#0a0c11');
      ctx.fillRect(labelX - 2, labelY - 12, textWidth + 4, 14);
      
      // Draw label text
      ctx.fillStyle = color;
      ctx.fillText(label, labelX, labelY);
    });

    // Draw playhead (only if visible in zoomed view)
    if (isPlaying || currentTime > 0) {
      const playheadX = timeToX(currentTime);
      if (playheadX >= 0 && playheadX <= width) {
        ctx.strokeStyle = readThemeColor('--rs-text', '#eef2f8');
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(playheadX, 0);
        ctx.lineTo(playheadX, height);
        ctx.stroke();
        
        // Draw playhead indicator circle
        ctx.fillStyle = readThemeColor('--rs-text', '#eef2f8');
        ctx.beginPath();
        ctx.arc(playheadX, centerY, 5, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw time labels (more labels when zoomed)
    ctx.fillStyle = readThemeColor('--rs-muted', '#9aa7b8');
    ctx.font = '11px monospace';
    const labelCount = Math.max(5, Math.floor(5 * zoom));
    for (let i = 0; i <= labelCount; i++) {
      const time = startTime + ((endTime - startTime) / labelCount) * i;
      const x = timeToX(time);
      if (x >= 0 && x <= width) {
        const timeStr = formatTime(time);
        ctx.fillText(timeStr, x + 5, height - 5);
      }
    }
  }, [waveformData, audioBuffer, markers, duration, currentTime, isPlaying, zoom, zoomOffset, generateHighResWaveform, cueOnly, colorMode]);

  // Update canvas on changes
  useEffect(() => {
    drawWaveform();
  }, [drawWaveform]);

  // Constrain zoom offset when zoom changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && duration) {
      // maxOffset: how far we can pan in virtual-canvas space (width * zoom - width)
      const maxOffset = Math.max(0, canvas.width * (zoom - 1));
      if (zoomOffset > maxOffset) {
        setZoomOffsetSynced(maxOffset);
      }
    }
  }, [zoom, duration, zoomOffset, setZoomOffsetSynced]);

  // Handle play/pause
  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    } else {
      // Start from cue_in if set
      const startTime = markers.cue_in || 0;
      audioRef.current.currentTime = Math.max(startTime, currentTime);
      audioRef.current.play().catch((err) => {
        console.error('AudioEditor: play failed:', err);
        setIsPlaying(false);
      });
      setIsPlaying(true);

      // Track playback position on every animation frame (~60fps) so the
      // playhead scrolls smoothly even when zoomed in. The old 100ms interval
      // only produced ~10 updates/sec, which looked jerky at high zoom.
      const trackPosition = () => {
        const audio = audioRef.current;
        if (!audio) return;

        setCurrentTime(audio.currentTime);

        // Stop at cue_out if set
        if (markers.cue_out && audio.currentTime >= markers.cue_out) {
          audio.pause();
          setIsPlaying(false);
          setCurrentTime(markers.cue_out);
          animationFrameRef.current = null;
          return;
        }

        animationFrameRef.current = requestAnimationFrame(trackPosition);
      };
      animationFrameRef.current = requestAnimationFrame(trackPosition);
    }
  };

  // Handle seek
  const handleSeek = (newTime) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Handle canvas mouse down (for dragging/clicking)
  const handleCanvasMouseDown = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !duration) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const clickX = (e.clientX - rect.left) * scaleX;

    // Record mouse-down position; dragging starts only after moving > 5px
    isMouseDownRef.current = true;
    isDraggingRef.current = false;
    dragStartXRef.current = clickX;
    dragStartOffsetRef.current = zoomOffsetRef.current;

    if (zoomRef.current > 1) {
      canvas.style.cursor = 'grab';
    }
  };

  // Handle canvas mouse move (for dragging)
  const handleCanvasMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !duration) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const currentX = (e.clientX - rect.left) * scaleX;

    // Promote mouse-down to a drag once the pointer moves more than 5px
    if (isMouseDownRef.current && zoomRef.current > 1 && !isDraggingRef.current) {
      if (Math.abs(currentX - dragStartXRef.current) > 5) {
        isDraggingRef.current = true;
        isMouseDownRef.current = false;
      }
    }

    if (isDraggingRef.current) {
      const deltaX = dragStartXRef.current - currentX;
      // maxOffset = full virtual width minus one canvas width
      const maxOffset = Math.max(0, canvas.width * (zoomRef.current - 1));
      const newOffset = Math.max(0, Math.min(maxOffset, dragStartOffsetRef.current + deltaX));
      setZoomOffsetSynced(newOffset);
      canvas.style.cursor = 'grabbing';
    } else if (zoomRef.current > 1) {
      canvas.style.cursor = 'grab';
    } else {
      canvas.style.cursor = 'crosshair';
    }
  };

  // Handle canvas mouse up
  const handleCanvasMouseUp = (e) => {
    const canvas = canvasRef.current;
    // If mouse was pressed but never dragged, treat as a seek click
    if (isMouseDownRef.current && !isDraggingRef.current) {
      handleCanvasClick(e);
    }
    isMouseDownRef.current = false;
    isDraggingRef.current = false;
    if (canvas) {
      canvas.style.cursor = zoomRef.current > 1 ? 'grab' : 'crosshair';
    }
  };

  // Handle mouse leaving canvas - cancel drag/click without seeking
  const handleCanvasMouseLeave = () => {
    isMouseDownRef.current = false;
    isDraggingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.cursor = zoomRef.current > 1 ? 'grab' : 'crosshair';
    }
  };

  // Handle canvas click to set playhead with accurate positioning
  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !duration) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const clickX = (e.clientX - rect.left) * scaleX;

    const clampedTime = getTimeFromCanvasX(clickX);

    handleSeek(clampedTime);
  };

  // Set marker at current position
  const setMarkerAtCurrentTime = (markerType) => {
    setMarkers(prev => ({
      ...prev,
      [markerType]: currentTime
    }));
  };

  // Clear marker
  const clearMarker = (markerType) => {
    setMarkers(prev => ({
      ...prev,
      [markerType]: markerType === 'cue_in' ? 0 : (markerType === 'cue_out' ? duration : null)
    }));
  };

  // Handle save
  const handleSave = async () => {
    try {
      const markerPayload = cueOnly
        ? (() => {
            const cueInRaw = Math.max(0, Number(markers.cue_in) || 0);
            const cueIn = duration > 0 ? Math.min(cueInRaw, Math.max(0, duration - 0.01)) : cueInRaw;
            let cueOut = Number(markers.cue_out);
            if (!Number.isFinite(cueOut) || cueOut <= 0) cueOut = null;
            if (cueOut != null && duration > 0) {
              cueOut = Math.min(cueOut, duration);
            }
            // File-end is "no trim". Persisting the browser duration as cue_out
            // would hard-cut on air if it is a few ms short of the real file.
            if (cueOut != null && duration > 0 && Math.abs(cueOut - duration) <= 0.05) {
              cueOut = null;
            }
            if (cueOut != null && cueOut <= cueIn) cueOut = null;
            return { cue_in: cueIn, cue_out: cueOut };
          })()
        : {
            cue_in: markers.cue_in,
            intro: markers.intro,
            start_next: markers.start_next,
            fade_out_start: markers.fade_out_start,
            extro: markers.fade_out_start, // Also update extro for compatibility
            cue_out: markers.cue_out,
          };

      if (saveDirectly) {
        await axios.put(
          `${API_URL}/api/assets/${asset.id}`,
          markerPayload,
          { withCredentials: true }
        );
      }

      if (onSave) {
        await onSave(markerPayload);
      }

      setSaved(true);
      setShowMarkerReview(false);
    } catch (error) {
      console.error('Error saving markers:', error);
      alert('Failed to save markers: ' + (error.response?.data?.message || error.message));
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      const activeTag = document.activeElement?.tagName;
      const isTypingField = activeTag === 'INPUT' || activeTag === 'TEXTAREA' || document.activeElement?.isContentEditable;
      if (isTypingField) return;

      if (event.key === 'ArrowLeft' && hasPrevious && onPrevious) {
        event.preventDefault();
        onPrevious();
      } else if (event.key === 'ArrowRight' && hasNext && onNext) {
        event.preventDefault();
        onNext();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, hasPrevious, hasNext, onPrevious, onNext]);

  // Format time helper
  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };

  if (!isOpen || !asset) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: '#22d3ee' }}>
            {cueOnly ? `Cue In / Cue Out: ${asset.title}` : `Edit Audio: ${asset.title}`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {!cueOnly && (
              <>
                <IconButton
                  onClick={onPrevious}
                  disabled={!hasPrevious}
                  sx={{ color: '#999' }}
                  title="Previous asset"
                >
                  <ChevronLeft />
                </IconButton>
                <IconButton
                  onClick={onNext}
                  disabled={!hasNext}
                  sx={{ color: '#999' }}
                  title="Next asset"
                >
                  <ChevronRight />
                </IconButton>
              </>
            )}
            <IconButton onClick={onClose} sx={{ color: '#999' }}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
          <Box>
            {showMarkerReview && (
              <Alert
                severity="warning"
                sx={{ mb: 2, bgcolor: 'rgba(245,158,11,0.08)', color: '#fbbf24', '& .MuiAlert-icon': { color: '#fbbf24' } }}
              >
                This file&apos;s length changed. Check {cueOnly ? 'cue in and cue out' : 'cue, intro, start-next, fade, and cue-out'} markers, then save.
              </Alert>
            )}
            {/* Waveform Canvas */}
            <Box
              sx={{
                position: 'relative',
                bgcolor: 'var(--rs-bg)',
                borderRadius: 1,
                overflow: 'hidden',
                mb: 3,
                cursor: 'crosshair',
                minHeight: 200,
              }}
            >
              <canvas
                ref={canvasRef}
                width={1200}
                height={200}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseLeave}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  cursor: zoom > 1 ? 'grab' : 'crosshair'
                }}
              />
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(10, 12, 17, 0.72)',
                    px: 4,
                  }}
                >
                  <LinearProgress sx={{ width: '100%', maxWidth: 360 }} />
                  <Typography sx={{ mt: 2, color: '#999' }}>Loading audio...</Typography>
                </Box>
              )}
            </Box>

            {/* Transport Controls */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
              <IconButton
                onClick={() => handleSeek(Math.max(0, currentTime - 5))}
                sx={{ color: '#22d3ee' }}
              >
                <SkipPrevious />
              </IconButton>
              <IconButton
                onClick={handlePlayPause}
                sx={{
                  bgcolor: isPlaying ? '#ff6b6b' : '#22d3ee',
                  color: 'white',
                  '&:hover': { bgcolor: isPlaying ? '#ff5252' : '#00b8e6' }
                }}
              >
                {isPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>
              <IconButton
                onClick={() => handleSeek(Math.min(duration, currentTime + 5))}
                sx={{ color: '#22d3ee' }}
              >
                <SkipNext />
              </IconButton>

              <Typography variant="body2" sx={{ color: '#999', minWidth: 100 }}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <VolumeDown fontSize="small" sx={{ color: '#666' }} />
                <Slider
                  value={volume}
                  onChange={(e, value) => {
                    setVolume(value);
                    if (audioRef.current) {
                      audioRef.current.volume = value / 100;
                    }
                  }}
                  min={0}
                  max={100}
                  sx={{ flex: 1 }}
                />
                <VolumeUp fontSize="small" sx={{ color: '#666' }} />
                <Typography variant="caption" sx={{ color: '#999', minWidth: 40 }}>
                  {volume}%
                </Typography>
              </Box>

              {/* Zoom Controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, borderLeft: '1px solid var(--rs-border)', pl: 2 }}>
                <Tooltip title="Zoom Out">
                  <IconButton
                    onClick={() => {
                      const newZoom = Math.max(1, zoom - 0.25);
                      const canvas = canvasRef.current;
                      if (canvas && duration) {
                        // Keep current playhead centred: zoomOffset = virtualX_of_playhead - width/2
                        const newOffset = Math.max(0, Math.min(
                          canvas.width * (newZoom - 1),
                          (currentTime / duration) * canvas.width * newZoom - canvas.width / 2
                        ));
                        setZoomSynced(newZoom);
                        setZoomOffsetSynced(newOffset);
                      } else {
                        setZoomSynced(newZoom);
                      }
                    }}
                    disabled={zoom <= 1}
                    sx={{ color: '#22d3ee' }}
                  >
                    <ZoomOut />
                  </IconButton>
                </Tooltip>
                <Slider
                  value={zoom}
                  onChange={(e, value) => {
                    const newZoom = value;
                    const canvas = canvasRef.current;
                    if (canvas && duration) {
                      const newOffset = Math.max(0, Math.min(
                        canvas.width * (newZoom - 1),
                        (currentTime / duration) * canvas.width * newZoom - canvas.width / 2
                      ));
                      setZoomSynced(newZoom);
                      setZoomOffsetSynced(newOffset);
                    } else {
                      setZoomSynced(newZoom);
                    }
                  }}
                  min={1}
                  max={8}
                  step={0.25}
                  sx={{ width: 100 }}
                />
                <Typography variant="caption" sx={{ color: '#999', minWidth: 50, textAlign: 'center' }}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom In">
                  <IconButton
                    onClick={() => {
                      const newZoom = Math.min(8, zoom + 0.25);
                      const canvas = canvasRef.current;
                      if (canvas && duration) {
                        const newOffset = Math.max(0, Math.min(
                          canvas.width * (newZoom - 1),
                          (currentTime / duration) * canvas.width * newZoom - canvas.width / 2
                        ));
                        setZoomSynced(newZoom);
                        setZoomOffsetSynced(newOffset);
                      } else {
                        setZoomSynced(newZoom);
                      }
                    }}
                    disabled={zoom >= 8}
                    sx={{ color: '#22d3ee' }}
                  >
                    <ZoomIn />
                  </IconButton>
                </Tooltip>
                {zoom > 1 && (
                  <Tooltip title="Reset Zoom">
                    <Button
                      size="small"
                      onClick={() => {
                        setZoomSynced(1);
                        setZoomOffsetSynced(0);
                      }}
                      sx={{ color: '#999', fontSize: '0.7rem' }}
                    >
                      Reset
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* Marker Controls */}
            <Grid container spacing={2}>
              {/* Cue In */}
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid #4caf50', p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: '#4caf50' }}>
                      Cue In (Start of Audio)
                    </Typography>
                  </Box>
                  {cueOnly && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Trims playback start. Break scheduling still uses the spot&apos;s 30/40s duration.
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      type="number"
                      size="small"
                      value={markers.cue_in || 0}
                      onChange={(e) => setMarkers(prev => ({ ...prev, cue_in: parseFloat(e.target.value) || 0 }))}
                      inputProps={{ step: 0.01, min: 0, max: duration }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      size="small"
                      onClick={() => setMarkerAtCurrentTime('cue_in')}
                      sx={{ color: '#4caf50' }}
                    >
                      Set @ {formatTime(currentTime)}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {formatTime(markers.cue_in)}
                  </Typography>
                </Card>
              </Grid>

              {!cueOnly && (
              <>
              {/* Intro */}
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid #2196f3', p: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#2196f3', mb: 1 }}>
                    Intro (User Set)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      type="number"
                      size="small"
                      value={markers.intro || ''}
                      onChange={(e) => setMarkers(prev => ({ ...prev, intro: e.target.value ? parseFloat(e.target.value) : null }))}
                      placeholder="Not set"
                      inputProps={{ step: 0.01, min: 0, max: duration }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      size="small"
                      onClick={() => setMarkerAtCurrentTime('intro')}
                      sx={{ color: '#2196f3' }}
                    >
                      Set @ {formatTime(currentTime)}
                    </Button>
                    {markers.intro !== null && (
                      <Button
                        size="small"
                        onClick={() => clearMarker('intro')}
                        sx={{ color: '#ff6b6b' }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                  {markers.intro !== null && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {formatTime(markers.intro)}
                    </Typography>
                  )}
                </Card>
              </Grid>

              {/* Start Next */}
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid #ff9800', p: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#ff9800', mb: 1 }}>
                    Start Next (Overlap Point)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      type="number"
                      size="small"
                      value={markers.start_next || ''}
                      onChange={(e) => setMarkers(prev => ({ ...prev, start_next: e.target.value ? parseFloat(e.target.value) : null }))}
                      placeholder="Not set"
                      inputProps={{ step: 0.01, min: 0, max: duration }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      size="small"
                      onClick={() => setMarkerAtCurrentTime('start_next')}
                      sx={{ color: '#ff9800' }}
                    >
                      Set @ {formatTime(currentTime)}
                    </Button>
                    {markers.start_next !== null && (
                      <Button
                        size="small"
                        onClick={() => clearMarker('start_next')}
                        sx={{ color: '#ff6b6b' }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                  {markers.start_next !== null && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {formatTime(markers.start_next)}
                    </Typography>
                  )}
                </Card>
              </Grid>

              {/* Fade Out Start */}
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid #ffc107', p: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#ffc107', mb: 1 }}>
                    Fade Out Start
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    If not set, plays to end. If Start Next is set, overlaps.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      type="number"
                      size="small"
                      value={markers.fade_out_start || ''}
                      onChange={(e) => setMarkers(prev => ({ ...prev, fade_out_start: e.target.value ? parseFloat(e.target.value) : null }))}
                      placeholder="Not set"
                      inputProps={{ step: 0.01, min: 0, max: duration }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      size="small"
                      onClick={() => setMarkerAtCurrentTime('fade_out_start')}
                      sx={{ color: '#ffc107' }}
                    >
                      Set @ {formatTime(currentTime)}
                    </Button>
                    {markers.fade_out_start !== null && (
                      <Button
                        size="small"
                        onClick={() => clearMarker('fade_out_start')}
                        sx={{ color: '#ff6b6b' }}
                      >
                        Clear
                      </Button>
                    )}
                  </Box>
                  {markers.fade_out_start !== null && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {formatTime(markers.fade_out_start)}
                    </Typography>
                  )}
                </Card>
              </Grid>
              </>
              )}

              {/* Cue Out */}
              <Grid item xs={12} md={cueOnly ? 6 : 12}>
                <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid #f44336', p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: '#f44336' }}>
                      Cue Out (End of Audio)
                    </Typography>
                  </Box>
                  {cueOnly && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Trims playback end. Leave at the file end unless the spot needs cutting.
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      type="number"
                      size="small"
                      value={markers.cue_out || duration}
                      onChange={(e) => setMarkers(prev => ({ ...prev, cue_out: parseFloat(e.target.value) || duration }))}
                      inputProps={{ step: 0.01, min: 0, max: duration }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      size="small"
                      onClick={() => setMarkerAtCurrentTime('cue_out')}
                      sx={{ color: '#f44336' }}
                    >
                      Set @ {formatTime(currentTime)}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setMarkers(prev => ({ ...prev, cue_out: duration }))}
                      sx={{ color: '#999' }}
                    >
                      Set to End
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {formatTime(markers.cue_out)}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<Close />}>
          Close
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saved}
          startIcon={saved ? <CheckCircle /> : <Save />}
          sx={{
            bgcolor: saved ? '#3a4456' : '#22d3ee',
            color: saved ? '#888' : 'white',
            '&:hover': { bgcolor: saved ? '#3a4456' : '#00b8e6' },
            '&.Mui-disabled': { bgcolor: '#3a4456', color: '#888' },
          }}
        >
          {saved ? 'Saved' : 'Save Markers'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
